import * as React from "react";
import { aktionsKnopfNode, useAktionsKnoepfe } from "./aktionsKnoepfe";
import { IcoLupe } from "../v3/vg3Icons";
import "../v3/vorgangV3.css";
import "./vorgangV4.css";

/**
 * Die Aktionsleiste am FUSS der Karte (Zeichnung 10.09.2026) — vier Wege,
 * etwas hinzuzufügen:
 *
 *     Neue Mail · Mail hinzufügen · Anruf · Dokument
 *
 * ══ WAS HIER NICHT MEHR STEHT ══════════════════════════════════════════════
 * Bis zum 11.09.2026 standen dazwischen „Alle Mails“ und „Alle Dokumente“ —
 * der Ersatz für den aufgelösten Verlauf. Seit das ARCHIV am Seitenende
 * zurück ist (`ArchivAbschnitt`), trägt es dieselbe Liste, vollständiger
 * (Notizen und Anrufe kommen hinzu) und an einer Stelle statt zweier. Drei
 * Wege zur selben Liste wären zwei zu viel.
 *
 * Annahme (PO hat die Knöpfe nie gesehen, im Gespräch ging es nur ums Archiv):
 * Das Archiv ERSETZT sie. Rückfallweg, falls anders entschieden wird — die
 * Knöpfe kehren zurück und springen ins Archiv mit vorgewähltem Filter, statt
 * einen eigenen Dialog zu öffnen; `AlleListeDialog` bleibt dafür bestehen,
 * auch wenn ihn seit dem Wegfall von V5 (20.09.2026) niemand mehr öffnet.
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * Die Leiste steht unten und nicht oben, weil sie der Rand des Arbeitsbereichs
 * ist, nicht sein Anfang: Man kommt her, um zu schreiben. Seit dem 13.09.2026
 * gibt es oben ZUSÄTZLICH das `⊕` in der Betreffzeile — bei einem langen
 * Vorgang liegt der Fuß zu weit unten. Beide Wirte lesen dieselbe Liste
 * (`aktionsKnoepfe.tsx`); hier steht nur noch, WIE sie aussieht: waagerecht,
 * als Knopfreihe. Seit das Nachschlagen ins Archiv gewandert ist, zeigen beide
 * Wirte dieselben vier Einträge — der Unterschied liegt nur noch in der Form.
 * Seit T-302 (29.10.2026) steht im Kopf und auf den To-do-Karten statt des
 * `⊕` das A des Austauschs; hier ist der Weg für Mail, Anruf, Dokument und
 * Termin damit wieder der eine.
 *
 * `todoId` bestimmt, WOHIN etwas geschrieben wird. Ausnahme bleibt der
 * Mail-VERSAND: `POST /mail/send` kennt nur `vorgangId`, eine hier verfasste
 * Mail hängt also immer am Vorgang. Der Knopf sagt das im Tooltip.
 */
/**
 * „Verlauf durchsuchen" — der letzte Knopf der Leiste (PO 18.09.2026).
 *
 * Er fügt nichts hinzu, sondern klappt Suchfeld und Filterchips über der
 * Kartenliste auf. Deshalb steht er hinter den vier Hinzufügen-Wegen und hinter
 * „Neuer Termin", und deshalb bleibt er auch OHNE Bearbeitungsrecht stehen:
 * Nachschlagen darf jeder, der die Seite sieht.
 */
export type SuchSchalter = { offen: boolean; onUmschalten: () => void };

type Props = {
    /** Fehlt er, trägt die Leiste keinen Such-Knopf (Strang ohne Verlauf). */
    suche?: SuchSchalter;
    vorgangId: number;
    todoId?: number;
    contactId?: number | null;
    contactName?: string | null;
    /** Bausteine des Betreff-Schemas ausgehender Mails. */
    vorgangNbr?: string | null;
    vertragNr?: string | null;
    risikoName?: string | null;
    /** Bereits verknüpfte messageIds — der Picker graut sie aus. */
    zugewieseneMessageIds: Set<string>;
    /** Schon im Strom liegende Dokumente — die Auswahl graut sie aus. */
    vorhandeneDokumentIds?: ReadonlySet<number>;
    canEdit: boolean;
    onGeaendert: () => void | Promise<void>;
};

export const AktionsLeisteV4: React.FC<Props> = ({ canEdit, suche, ...optionen }) => {
    const { knoepfe, elemente } = useAktionsKnoepfe(optionen);

    /*
     * Hinzufügen darf nur, wer Bearbeitungsrecht hat — und seit dem Archiv ist
     * jeder Eintrag ein Hinzufügen-Eintrag. Ohne Recht bleibt die Reihe darum
     * leer und es steht nur der Hinweis da; nachgeschlagen wird im Archiv am
     * Seitenende, das jeder sehen darf, der die Seite sieht.
     */
    const sichtbar = canEdit ? knoepfe : knoepfe.filter((k) => !k.nurMitRecht);

    return (
        <div className="vg4-aktionen">
            {sichtbar.map((k) => aktionsKnopfNode(k, { className: "vg3-btn", mitAnzahl: true }))}

            {suche && (
                <button
                    type="button"
                    className={`vg3-btn${suche.offen ? " an" : ""}`}
                    aria-pressed={suche.offen}
                    title="Suchfeld und Filter über dem Verlauf ein- und ausblenden"
                    onClick={suche.onUmschalten}
                >
                    <IcoLupe size={15} />
                    Verlauf durchsuchen
                </button>
            )}

            {!canEdit && (
                <span className="vg3-leer">Ohne Bearbeitungsrecht lässt sich hier nichts hinzufügen.</span>
            )}

            {elemente}
        </div>
    );
};
